import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Sun, Moon, Menu, X } from 'lucide-react';

export default function Navbar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="border-b border-border backdrop-blur-md bg-background/80 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2">
          <img
            src="/pinaka-logo.png"
            alt="Pinaka Education"
            className="h-10 sm:h-12 w-auto"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-3">
          <Link to="/test-series" className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2">
            Test Series
          </Link>
          <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2">
            About
          </Link>
          <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2">
            Contact
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full"
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? (
              <Sun className="w-5 h-5 text-warning" />
            ) : (
              <Moon className="w-5 h-5 text-primary" />
            )}
          </Button>
          {user ? (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                data-testid="nav-dashboard-btn"
              >
                Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                data-testid="nav-logout-btn"
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                data-testid="nav-login-btn"
              >
                Login
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="bg-primary hover:bg-primary/90 text-white font-semibold shadow-glow"
                data-testid="nav-register-btn"
              >
                Sign Up
              </Button>
            </>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <div className="flex md:hidden items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full"
          >
            {theme === 'dark' ? (
              <Sun className="w-5 h-5 text-warning" />
            ) : (
              <Moon className="w-5 h-5 text-primary" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-md px-4 py-4 space-y-2">
          <Link to="/test-series" className="block px-3 py-2 text-sm rounded-md hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
            Test Series
          </Link>
          <Link to="/about" className="block px-3 py-2 text-sm rounded-md hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
            About
          </Link>
          <Link to="/contact" className="block px-3 py-2 text-sm rounded-md hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
            Contact
          </Link>
          {user ? (
            <>
              <Link to="/dashboard" className="block px-3 py-2 text-sm rounded-md hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
                Dashboard
              </Link>
              <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted text-destructive">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="block px-3 py-2 text-sm rounded-md hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
                Login
              </Link>
              <Link to="/register" className="block px-3 py-2 text-sm font-semibold text-primary rounded-md hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
                Sign Up
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
